import React, { useCallback, useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { Chart, InvestmentDetailActions, Table } from "components/Investment-detail";
import { SelectDealContent } from "components/Prompts/SelectDealContent";
import { useActionHook, useDevice, useModals, useNavigate, usePrompt } from "hooks";
import { useDealActions, useTaskDealsSelect } from "store";
import { IDealsWithInvestmentDetails } from "store/types";
import pickFiles from "utils/pick-files";
import { toastify } from "utils/tyostify";

import { FreemiumMessages } from "../../../constants";
import { DealNameSelector } from "./DealNameSelector";
import { EmptyLookThrough } from "./EmptyLookThrough";

interface LookThroughProps {
  deals: IDealsWithInvestmentDetails[];
  selectedDeal: IDealsWithInvestmentDetails | null;
  handleDealChange: (deal: IDealsWithInvestmentDetails) => void;
}

export const LookThrough: React.FC<LookThroughProps> = ({ deals, selectedDeal, handleDealChange }) => {
  const navigate = useNavigate();
  const { infoModal } = useModals();
  const openPrompt = usePrompt();
  const accountDeals = useTaskDealsSelect() || [];
  const { isMobile } = useDevice();
  const { getInvestmentDetailsFieldsAction, createInvestmentDetailFromAIAction, getInvestmentDetailsAction } =
    useDealActions();
  const [refetch, setRefetch] = useState(false);

  // Fetch columns
  const { action: getColumns, data: columns = [] } = useActionHook(getInvestmentDetailsFieldsAction);

  // Fetch table data
  const {
    action: getTableData,
    data: tableData,
    loading: tableLoading,
    setData: setTableData
  } = useActionHook(getInvestmentDetailsAction);
  const { action: createInvestmentDetailFromAi } = useActionHook(createInvestmentDetailFromAIAction, {
    onSuccess: () => {
      setRefetch(true);
    },
    onReject: (err) => {
      toastify({ message: err.message, type: "error" });
    }
  });

  // Transform chart settings to chart configurations (same as main LookThrough page)
  const chartDataFormation = useMemo(() => {
    if (!selectedDeal) return [];
    if (!selectedDeal.chart_settings || typeof selectedDeal.chart_settings !== "object") {
      return [];
    }
    return Object.entries(selectedDeal.chart_settings).map(([key, config]) => ({
      chartName: key,
      groupBy: config.group_by,
      valueBy: config.value_by
    }));
  }, [selectedDeal]);

  // Fetch columns on mount
  useEffect(() => {
    getColumns();
  }, []);

  useEffect(() => {
    if (selectedDeal?.id) {
      getTableData(selectedDeal.id);
    }
  }, [selectedDeal?.id]);

  const refetchTableData = useCallback(() => {
    if (selectedDeal?.id) {
      getTableData(selectedDeal.id);
    }
  }, [selectedDeal?.id, getTableData]);

  const handleOpenInNew = useCallback(() => {
    if (!selectedDeal) return;
    isMobile &&
      infoModal({
        title: FreemiumMessages.BEST_PRACTICE_RECOMMENDATION_TITLE,
        msg: FreemiumMessages.BEST_PRACTICE_RECOMMENDATION_MESSAGE,
        buttonText: "Continue"
      });
    navigate({ route: "dealLookThrough", params: { deal_id: selectedDeal.id } });
  }, [navigate, selectedDeal, infoModal]);

  const handlePreviousDeal = useCallback(() => {
    if (!deals.length || !selectedDeal) return;
    const currentIndex = deals.findIndex((deal) => deal.id === selectedDeal.id);
    if (currentIndex > 0) {
      const previousDeal = deals[currentIndex - 1];
      handleDealChange(previousDeal);
    }
  }, [selectedDeal, deals]);

  const handleNextDeal = useCallback(() => {
    if (!deals.length || !selectedDeal) return;
    const currentIndex = deals.findIndex((deal) => deal.id === selectedDeal.id);
    if (currentIndex < deals.length - 1) {
      const nextDeal = deals[currentIndex + 1];
      handleDealChange(nextDeal);
    }
  }, [selectedDeal, deals]);

  const canGoPrevious =
    selectedDeal && deals.length ? deals.findIndex((deal) => deal.id === selectedDeal.id) > 0 : false;
  const canGoNext =
    selectedDeal && deals.length ? deals.findIndex((deal) => deal.id === selectedDeal.id) < deals.length - 1 : false;

  const handleFileUpload = useCallback(
    async (preselectedFileList?: FileList) => {
      if (!accountDeals.length) {
        toastify({ message: "You need to add at least one deal", type: "error" });
        return;
      }
      const fileList =
        preselectedFileList ||
        (await pickFiles({
          multiple: true,
          accept: "application/pdf"
        }));

      if (!fileList?.length) return;
      const deal = await openPrompt({
        Component: SelectDealContent,
        props: {}
      });
      if (deal) {
        await createInvestmentDetailFromAi({
          files: Array.from(fileList),
          dealId: deal.id
        });
      }
    },
    [openPrompt, deals, createInvestmentDetailFromAi]
  );

  // Show empty state if no deals

  if (!deals.length) {
    return (
      <Stack
        sx={{
          width: "100%",
          p: "16px",
          gap: "16px",
          bgcolor: "white",
          borderRadius: "8px",
          boxShadow: "0px 1px 2px 0px rgba(18, 18, 23, 0.1)"
        }}
        alignItems="stretch"
        direction="column"
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems={isMobile ? "flex-start" : "center"}
          gap={isMobile ? "10px" : 0}
        >
          <Stack direction="row" alignItems="center" gap="10px">
            <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#071f35" }}>Look Through</Typography>
            <IconButton sx={{ width: "20px", height: "20px", padding: 0 }} onClick={handleOpenInNew}>
              <OpenInNewIcon sx={{ width: "16px", height: "16px", color: "#071F35" }} />
            </IconButton>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
            onClick={() => handleFileUpload()}
            sx={{
              minHeight: "24px",
              height: "24px",
              borderColor: "#7b92a9",
              color: "#071f35",
              fontSize: "12px",
              fontWeight: 400,
              textTransform: "none",
              borderRadius: "32px",
              px: "8px",
              py: "4px",
              minWidth: "auto",
              "&:hover": {
                borderColor: "#071f35",
                backgroundColor: "transparent"
              }
            }}
          >
            Upload Data
          </Button>
        </Stack>

        {/* Empty State */}
        <EmptyLookThrough onFileUpload={handleFileUpload} />
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        width: "100%",
        p: "16px",
        gap: "16px",
        bgcolor: "white",
        borderRadius: "8px",
        boxShadow: "0px 1px 2px 0px rgba(18, 18, 23, 0.1)"
      }}
      alignItems="stretch"
      direction="column"
    >
      {/* Header */}
      {isMobile ? (
        <Stack direction="column" gap="12px">
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap="10px">
            <Stack direction="row" alignItems="center" gap="10px">
              <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#071f35" }}>Look through</Typography>
              <IconButton sx={{ width: "20px", height: "20px", padding: 0 }} onClick={handleOpenInNew}>
                <OpenInNewIcon sx={{ width: "16px", height: "16px", color: "#071F35" }} />
              </IconButton>
            </Stack>
            <Stack direction="row" alignItems="center" gap="8px">
              {selectedDeal?.id && !!columns.length && (
                <InvestmentDetailActions
                  dealId={selectedDeal.id}
                  columns={columns}
                  onChartsRefetch={() => setRefetch(true)}
                  onTableRefetch={refetchTableData}
                />
              )}
              <Button
                variant="outlined"
                startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
                onClick={() => handleFileUpload()}
                sx={{
                  minHeight: "24px",
                  height: "24px",
                  borderColor: "#7b92a9",
                  color: "#071f35",
                  fontSize: "12px",
                  fontWeight: 400,
                  textTransform: "none",
                  borderRadius: "32px",
                  px: "8px",
                  py: "4px",
                  minWidth: "auto",
                  "&:hover": {
                    borderColor: "#071f35",
                    backgroundColor: "transparent"
                  }
                }}
              >
                Upload data
              </Button>
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center" gap="10px" justifyContent="center">
            <IconButton
              onClick={handlePreviousDeal}
              disabled={!canGoPrevious}
              sx={{
                width: "24px",
                height: "24px",
                padding: "4px",
                backgroundColor: "#f5f6f7",
                borderRadius: "32px",
                opacity: canGoPrevious ? 1 : 0.5
              }}
            >
              <KeyboardArrowLeftIcon sx={{ fontSize: "16px", color: "#071F35" }} />
            </IconButton>
            {!!deals.length && (
              <DealNameSelector deals={deals} selectedDealId={selectedDeal?.id} onDealChange={handleDealChange} />
            )}
            <IconButton
              onClick={handleNextDeal}
              disabled={!canGoNext}
              sx={{
                width: "24px",
                height: "24px",
                padding: "4px",
                backgroundColor: "#f5f6f7",
                borderRadius: "32px",
                opacity: canGoNext ? 1 : 0.5
              }}
            >
              <KeyboardArrowRightIcon sx={{ fontSize: "16px", color: "#071F35" }} />
            </IconButton>
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" gap="10px" flex={1}>
            <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#071f35" }}>Look Through</Typography>
            <IconButton sx={{ width: "20px", height: "20px", padding: 0 }} onClick={handleOpenInNew}>
              <OpenInNewIcon sx={{ width: "16px", height: "16px", color: "#071F35" }} />
            </IconButton>
          </Stack>

          <Stack direction="row" alignItems="center" gap="10px" flex={1} justifyContent="center">
            <IconButton
              onClick={handlePreviousDeal}
              disabled={!canGoPrevious}
              sx={{
                width: "24px",
                height: "24px",
                padding: "4px",
                backgroundColor: "#f5f6f7",
                borderRadius: "32px",
                opacity: canGoPrevious ? 1 : 0.5
              }}
            >
              <KeyboardArrowLeftIcon sx={{ fontSize: "16px", color: "#071F35" }} />
            </IconButton>
            {!!deals.length && (
              <DealNameSelector deals={deals} selectedDealId={selectedDeal?.id} onDealChange={handleDealChange} />
            )}
            <IconButton
              onClick={handleNextDeal}
              disabled={!canGoNext}
              sx={{
                width: "24px",
                height: "24px",
                padding: "4px",
                backgroundColor: "#f5f6f7",
                borderRadius: "32px",
                opacity: canGoNext ? 1 : 0.5
              }}
            >
              <KeyboardArrowRightIcon sx={{ fontSize: "16px", color: "#071F35" }} />
            </IconButton>
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="flex-end" gap="8px" flex={1}>
            {selectedDeal?.id && !!columns.length && (
              <InvestmentDetailActions
                dealId={selectedDeal.id}
                columns={columns}
                onChartsRefetch={() => setRefetch(true)}
                onTableRefetch={refetchTableData}
              />
            )}
            <Button
              variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
              onClick={() => handleFileUpload()}
              sx={{
                minHeight: "24px",
                height: "24px",
                borderColor: "#7b92a9",
                color: "#071f35",
                fontSize: "12px",
                fontWeight: 400,
                textTransform: "none",
                borderRadius: "32px",
                px: "8px",
                py: "4px",
                minWidth: "auto",
                "&:hover": {
                  borderColor: "#071f35",
                  backgroundColor: "transparent"
                }
              }}
            >
              Upload Data
            </Button>
          </Stack>
        </Stack>
      )}

      {/* Charts */}
      {!deals.length && !selectedDeal ? (
        <Stack
          sx={{
            width: "100%",
            p: "16px",
            gap: "16px",
            bgcolor: "white",
            borderRadius: "8px",
            boxShadow: "0px 1px 2px 0px rgba(18, 18, 23, 0.1)"
          }}
          alignItems="stretch"
          direction="column"
        >
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems={isMobile ? "flex-start" : "center"}
            gap={isMobile ? "10px" : 0}
          >
            <Stack direction="row" alignItems="center" gap="10px">
              <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#071f35" }}>Look Through</Typography>
              <IconButton sx={{ width: "20px", height: "20px", padding: 0 }} onClick={handleOpenInNew}>
                <OpenInNewIcon sx={{ width: "16px", height: "16px", color: "#071F35" }} />
              </IconButton>
            </Stack>

            <Button
              variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
              onClick={() => handleFileUpload()}
              sx={{
                minHeight: "24px",
                height: "24px",
                borderColor: "#7b92a9",
                color: "#071f35",
                fontSize: "12px",
                fontWeight: 400,
                textTransform: "none",
                borderRadius: "32px",
                px: "8px",
                py: "4px",
                minWidth: "auto",
                "&:hover": {
                  borderColor: "#071f35",
                  backgroundColor: "transparent"
                }
              }}
            >
              Upload Data
            </Button>
          </Stack>

          {/* Empty State */}
          <EmptyLookThrough onFileUpload={handleFileUpload} />
        </Stack>
      ) : (
        <Stack gap="16px">
          <Stack
            direction={isMobile ? "column" : "row"}
            gap="20px"
            sx={{
              "& > *": {
                flex: 1,
                maxWidth: isMobile ? "100%" : "calc(50% - 10px)"
              }
            }}
          >
            {!!selectedDeal &&
              !!chartDataFormation.length &&
              !!selectedDeal.id &&
              chartDataFormation.map((config, index) => (
                <Chart
                  dealId={selectedDeal.id}
                  reqConfig={config}
                  columns={columns}
                  key={index}
                  isNeedRefetch={refetch}
                  invalidateRefetch={() => setRefetch(false)}
                  isFreemium
                />
              ))}
          </Stack>

          {/* Table */}
          {selectedDeal && !!columns.length && !!selectedDeal.id && (
            <Table
              columns={columns}
              setRefetch={() => setRefetch(true)}
              dealId={selectedDeal.id}
              isFreemium
              data={tableData}
              loading={tableLoading}
              setData={setTableData}
              refetchTableData={refetchTableData}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};
